import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Section } from "../components/Common";
import AuthBrandHeader from "../components/AuthBrandHeader";
import { toast } from "sonner";
import { errorMessage } from "../lib/errors";

export default function SignupPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerEmail, startGoogle } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error(t("auth.password_min"));
    setLoading(true);
    try {
      await registerEmail(form.email, form.password, form.name, i18n.language);
      toast.success(t("auth.welcome_toast"));
      const next = searchParams.get("next");
      navigate(next && next.startsWith("/") ? next : "/profile");
    } catch (e) {
      toast.error(errorMessage(e, t("auth.signup_failed")));
    } finally { setLoading(false); }
  };

  return (
    <>
      <AuthBrandHeader
        eyebrow={t("auth.signup_eyebrow")}
        title={t("auth.signup_title")}
        subtitle={t("auth.signup_subtitle")}
      />
      <Section>
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
        <div className="card-surface p-6" data-testid="signup-form">
          <form onSubmit={submit} className="grid gap-3">
            <input placeholder={t("auth.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="signup-name" />
            <input type="email" placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="signup-email" />
            <input type="password" placeholder={t("auth.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="bg-[#0F1116] border border-white/10 rounded-lg px-4 py-3 text-sm" data-testid="signup-password" />
            <button type="submit" disabled={loading} className="btn-primary text-sm" data-testid="signup-submit">{loading ? "…" : t("auth.sign_up")}</button>
          </form>
          <div className="mt-4 text-sm text-white/70">
            <Link to={`/login${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next"))}` : ""}`} data-testid="signup-to-login">{t("auth.have_account")} {t("auth.sign_in")}</Link>
          </div>
          <p className="mt-4 text-xs text-white/55 leading-relaxed" data-testid="signup-legal-ack">
            {t("auth.legal_ack_prefix", "By continuing, you agree to the")}{" "}
            <Link
              to="/terms"
              className="underline underline-offset-2 decoration-white/30 hover:text-white hover:decoration-white/60"
              data-testid="signup-legal-terms"
            >
              {t("auth.legal_ack_terms", "Terms of Use")}
            </Link>{" "}
            {t("auth.legal_ack_conjunction", "and acknowledge the")}{" "}
            <Link
              to="/privacy"
              className="underline underline-offset-2 decoration-white/30 hover:text-white hover:decoration-white/60"
              data-testid="signup-legal-privacy"
            >
              {t("auth.legal_ack_privacy", "Privacy Policy")}
            </Link>
            {t("auth.legal_ack_suffix", ".")}
          </p>
        </div>
        <div className="card-surface p-6 flex flex-col gap-3">
          <div className="text-xs uppercase tracking-[0.24em] gold-text">{t("nav.sso")}</div>
          <button onClick={startGoogle} className="btn-secondary text-sm inline-flex items-center justify-center gap-2" data-testid="signup-google">{t("auth.google")}</button>
          <p className="text-xs text-white/55" data-testid="signup-future-sso">{t("auth.future_sso")}</p>
        </div>
      </div>
    </Section>
    </>
  );
}
